import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { getAdobeAccountEventTracking } from '../../../services/trackingService';
import { RetryPaymentMethod, RetryPaymentStatus } from '../../../types';
import Notification from '../notification/Notification';

export type RetryPaymentNotificationProps = {
  retryPaymentStatus?: RetryPaymentStatus;
  retryPaymentMethod?: RetryPaymentMethod;
  orderId?: string;
};

const RetryPaymentNotification: React.FC<RetryPaymentNotificationProps> = ({
  retryPaymentStatus,
  retryPaymentMethod,
  orderId,
}) => {
  const { t } = useTranslation();
  useEffect(() => {
    const timer = setTimeout(() => {
      const adbAccEvent = getAdobeAccountEventTracking();
      adbAccEvent.track('retry_payment_status', {
        payment_method: retryPaymentMethod,
        order_id: orderId,
        payment_status: retryPaymentStatus,
      });
    }, 5000);
    return () => clearTimeout(timer);
  }, [retryPaymentStatus, retryPaymentMethod, orderId]);

  switch (retryPaymentStatus) {
    case 'SUCCESSFUL':
      return (
        <Notification
          id="retryPaymentSuccessfulNotification"
          ariaLabel={t('mzp.order.detail.retry.payment.notification.accessibility.label')}
          type="success">
          {t('mzp.order.detail.payment.retried.successful')}
        </Notification>
      );
    case 'PROCESSING':
    case 'UNKNOWN':
    case 'SERVICE_NOT_AVAILABLE':
      return (
        <Notification
          id="retryPaymentInfoNotification"
          ariaLabel={t('mzp.order.detail.retry.payment.notification.accessibility.label')}
          type="info">
          {t('mzp.order.detail.payment.retried.processing')}
        </Notification>
      );
    case 'CANCELLED':
      return (
        <Notification
          id="retryPaymentInfoNotification"
          ariaLabel={t('mzp.order.detail.retry.payment.notification.accessibility.label')}
          type="info">
          {t('mzp.order.detail.payment.retried.cancelled')}
        </Notification>
      );
    case 'UNSUCCESSFUL':
      return (
        <Notification
          id="retryPaymentErrorNotification"
          ariaLabel={t('mzp.order.detail.retry.payment.notification.accessibility.label')}
          type="warning">
          {t('mzp.order.detail.payment.retried.unsuccessful')}
        </Notification>
      );
  }

  return null;
};

export default RetryPaymentNotification;
